import React, { useEffect, useState } from 'react'
import { fmt, pickCsvFile } from '../api.js'
import { portal } from '../portalApi.js'
import NoteDrawer from '../components/NoteDrawer.jsx'
import { CompanyPicker, costGet, useCompany, useMeta, useToast } from './CostCommon.jsx'

// 원가관리 · 통합 재고수불부 (월별 총평균).
// 단가는 달마다 다시 낸다 — 원본 시트가 그렇게 하고, 연간으로 한 번만 내면 그 해
// 매입가가 오르내린 만큼 기말 평가액이 어긋난다.
// 사업부·창고·정상/불량으로 좁혀 볼 수 있지만, 단가는 좁혀 봐도 품목 전체 총평균이다.

export default function CostInventory({ year, month, onMonthChange, currentUserId, autoOpenNoteId, onAutoOpened }) {
  const [company, setCompany] = useCompany()
  const [meta] = useMeta()
  const [type, setType] = useState('전체')
  const [location, setLocation] = useState('')
  const [condition, setCondition] = useState('')
  const [filter, setFilter] = useState('')
  const [data, setData] = useState(null)
  const [locs, setLocs] = useState(null)
  const [showLoc, setShowLoc] = useState(false)
  const [showTrend, setShowTrend] = useState(false)
  const [err, setErr] = useState('')
  const [notify, toast] = useToast()

  // 기준월이 그 해가 아니면 연말까지 본다 (사이드바 기준월과 화면 연도가 어긋날 때)
  const through = month && month.slice(0, 4) === String(year) ? month : `${year}-12`

  const load = () => {
    setErr('')
    costGet('/inventory/ledger', { year, month: through, company, type, location, condition })
      .then(setData).catch((e) => setErr(e.message))
    costGet('/inventory/locations', { year, month: through, company })
      .then(setLocs).catch(() => setLocs(null))
  }
  useEffect(() => { load() }, [year, through, company, type, location, condition])

  const importOpening = () => pickCsvFile(async (rows) => {
    try {
      const res = await portal.post('/api/cost/opening/bulk', { year, company, rows })
      notify(`기초재고 ${res.upserted}건 반영${res.missing.length ? `, 미등록 ${res.missing.length}건 제외` : ''}`)
      load()
    } catch (e) { setErr(e.message) }
  })

  const importCount = () => pickCsvFile(async (rows) => {
    try {
      const res = await portal.post('/api/cost/count/bulk', { year, company, rows })
      notify(`재고실사 ${res.upserted}건 반영${res.missing.length ? `, 미등록 ${res.missing.length}건 제외` : ''}`)
      load()
    } catch (e) { setErr(e.message) }
  })

  if (err && !data) return <div className="error-box">{err}</div>
  if (!data) return <div className="muted">불러오는 중…</div>
  const t = data.totals

  const q = filter.trim().toLowerCase()
  const rows = data.rows.filter((r) =>
    !q || [r.sku, r.alt_sku, r.name, r.option_name, r.category].some((v) => (v || '').toLowerCase().includes(q)))
  const locOptions = [...new Set((meta?.locations || [])
    .filter((l) => !company || l.company === company).map((l) => l.name))]
  const trend = (data.monthly || []).filter((m) =>
    m.closingQty || m.inQty || m.salesQty || m.prodQty)

  return (
    <>
      <div style={{ position: 'relative' }}>
        <h2>{year}년 통합 재고수불부 — {through} 기준 (월별 총평균)</h2>
        <NoteDrawer target="inventory" screenKey="cost_inventory"
          context={{ year }} contextLabel={`${year}년`}
          onNavigate={(ctx) => ctx?.year && onMonthChange?.(`${ctx.year}-01`)}
          autoOpenNoteId={autoOpenNoteId} onAutoOpened={onAutoOpened}
          currentUserId={currentUserId} />
      </div>
      {err && <div className="error-box">{err}</div>}
      <div className="row" style={{ marginBottom: 12 }}>
        <CompanyPicker value={company} onChange={setCompany} meta={meta} />
        <select value={type} onChange={(e) => setType(e.target.value)}>
          {['전체', ...(meta?.itemTypes || [])].map((v) => <option key={v}>{v}</option>)}
        </select>
        <select value={location} onChange={(e) => setLocation(e.target.value)}>
          <option value="">전체 창고</option>
          {locOptions.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select value={condition} onChange={(e) => setCondition(e.target.value)}>
          <option value="">정상+불량</option>
          {(meta?.conditions || []).map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <input placeholder="검색 (품번/상품명)" style={{ width: 220 }}
          value={filter} onChange={(e) => setFilter(e.target.value)} />
        <button className="btn secondary" onClick={() => setShowTrend(!showTrend)}>월별 추이</button>
        <button className="btn secondary" onClick={() => setShowLoc(!showLoc)}>장소별 재고금액</button>
      </div>
      <div className="row" style={{ marginBottom: 12 }}>
        <button className="btn secondary" onClick={importOpening} disabled={!company}>
          기초재고 CSV (sku, qty, amount, condition)
        </button>
        <button className="btn secondary" onClick={importCount} disabled={!company}>
          재고실사 CSV (sku, qty, location, condition)
        </button>
        {!company && <span className="muted">업로드는 사업부를 골라야 합니다</span>}
      </div>

      {showTrend && (
        <div className="panel">
          <h3>월별 추이</h3>
          {trend.length === 0 ? <div className="muted">움직임이 없습니다</div> : (
            <table style={{ maxWidth: 980 }}>
              <thead>
                <tr>
                  <th>월</th><th>이월수량</th><th>이월금액</th>
                  <th>생산</th><th>제조원가</th><th>매입수량</th><th>매입금액</th>
                  <th>판매출고</th><th>타계정출고</th><th>기말수량</th><th>기말금액</th>
                </tr>
              </thead>
              <tbody>
                {trend.map((m) => (
                  <tr key={m.month}>
                    <td className="c">{m.month}월</td>
                    <td>{fmt(m.openQty)}</td><td>{fmt(m.openAmt)}</td>
                    <td>{fmt(m.prodQty)}</td><td>{fmt(m.prodCost)}</td>
                    <td>{fmt(m.inQty)}</td><td>{fmt(m.inAmt)}</td>
                    <td>{fmt(m.salesAmt)}</td><td>{fmt(m.otherOutAmt)}</td>
                    <td>{fmt(m.closingQty)}</td><td><b>{fmt(m.closingAmt)}</b></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="hint">
            달마다 총평균단가를 다시 내고, 그 달 기말이 다음 달 이월이 됩니다.
            원본 수불부의 월별 기말과 이 표를 맞춰 보세요.
            {(location || condition) && ' (창고·상태를 걸러 볼 때는 월별 추이를 내지 않습니다)'}
          </div>
        </div>
      )}

      {showLoc && locs && (
        <div className="panel">
          <h3>장소별 재고금액</h3>
          {locs.estimatedLocations.length > 0 && (
            <div className="hint warn-hint" style={{ marginTop: 0 }}>
              실사가 없는 창고는 <b>당기증감</b>(입고−출고)만 보여줍니다 — 기초재고에는 창고 정보가 없어
              전기부터 있던 물건이 나간 창고는 음수로 나옵니다. 창고별 실사를 올리면 실사 수량으로 바뀝니다.
              현재 추정 중인 창고: {locs.estimatedLocations.join(', ')}
            </div>
          )}
          <table style={{ maxWidth: 760 }}>
            <thead><tr><th className="l">창고</th><th>상태</th><th>근거</th><th>수량</th><th>당기증감</th><th>실사</th><th>금액</th><th>품목</th></tr></thead>
            <tbody>
              {locs.rows.map((r) => (
                <tr key={`${r.location}|${r.condition}`}>
                  <td className="l">{r.location}</td>
                  <td className="c">{r.condition}</td>
                  <td className="c">{r.basis === '실사' ? <span className="ok">실사</span> : <span className="warn">당기증감</span>}</td>
                  <td className={r.qty < 0 ? 'warn' : ''}>{fmt(r.qty)}</td>
                  <td>{fmt(r.moveQty)}</td>
                  <td>{r.countQty === null ? '-' : fmt(r.countQty)}</td>
                  <td>{fmt(r.amount)}</td>
                  <td>{fmt(r.items)}</td>
                </tr>
              ))}
              <tr className="total"><td className="l" colSpan={6}>합계</td><td>{fmt(locs.totalAmt)}</td><td /></tr>
            </tbody>
          </table>
          <div className="hint">
            수불부 기말 재고금액 {fmt(locs.ledgerClosingAmt)}원과의 차이 {fmt(locs.ledgerClosingAmt - locs.totalAmt)}원
            — 창고별 실사가 다 들어오기 전에는 벌어지는 게 정상입니다.
          </div>
        </div>
      )}

      <div className="hint">
        총평균단가 = (이월금액 + 당월 제조원가 + 당월 매입금액) ÷ (이월수량 + 생산수량 + 매입수량)을 <b>달마다</b> 다시 냅니다.
        금액이 안 붙은 입고(조정입고 등)는 분모에서도 빼 단가가 눌리지 않게 합니다.
        타계정출고와 기말재고는 그 달 단가로 평가됩니다.
      </div>
      <div className="panel">
        <table>
          <thead>
            <tr>
              {!company && <th rowSpan={2} className="l">사업부</th>}
              <th rowSpan={2} className="l">품번</th>
              <th rowSpan={2} className="l">상품명</th>
              <th colSpan={2}>기초</th>
              <th colSpan={2}>당기생산</th>
              <th colSpan={2}>매입/기타입고</th>
              <th colSpan={2}>판매·조정출고</th>
              <th colSpan={2}>타계정출고</th>
              <th rowSpan={2}>기준월 단가</th>
              <th colSpan={2}>기말재고</th>
              <th colSpan={2}>실사대사</th>
            </tr>
            <tr>
              <th>수량</th><th>금액</th>
              <th>수량</th><th>제조원가</th>
              <th>수량</th><th>금액</th>
              <th>수량</th><th>금액</th>
              <th>수량</th><th>금액</th>
              <th>수량</th><th>금액</th>
              <th>실사</th><th>차이</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={19} className="c muted">데이터가 없습니다</td></tr>}
            {rows.slice(0, 800).map((r) => (
              <tr key={r.item_id}>
                {!company && <td className="l">{r.company_name}</td>}
                <td className="l">{r.sku}</td>
                <td className="l" title={r.option_name}>{r.name} {r.option_name && <span className="muted">{r.option_name}</span>}</td>
                <td>{fmt(r.openQty)}</td><td>{fmt(r.openAmt)}</td>
                <td>{fmt(r.prodQty)}</td><td>{fmt(r.prodCost)}</td>
                <td>{fmt(r.inQty)}</td><td>{fmt(r.inAmt)}</td>
                <td>{fmt(r.salesQty)}</td><td>{fmt(r.salesAmt)}</td>
                <td>{fmt(r.otherOutQty)}</td><td>{fmt(r.otherOutAmt)}</td>
                <td>{fmt(r.avgCost, 1)}</td>
                <td className={r.closingQty < 0 ? 'warn' : ''}>{fmt(r.closingQty)}</td>
                <td>{fmt(r.closingAmt)}</td>
                <td>{r.countQty === null ? '-' : fmt(r.countQty)}</td>
                <td className={r.countDiff ? 'warn' : ''}>{r.countDiff === null ? '-' : fmt(r.countDiff)}</td>
              </tr>
            ))}
            {rows.length > 0 && (
              <tr className="total">
                <td className="l" colSpan={company ? 3 : 4}>합계 (검색 무관 전체)</td>
                <td>{fmt(t.openQty)}</td><td>{fmt(t.openAmt)}</td>
                <td>{fmt(t.prodQty)}</td><td>{fmt(t.prodCost)}</td>
                <td>{fmt(t.inQty)}</td><td>{fmt(t.inAmt)}</td>
                <td>{fmt(t.salesQty)}</td><td>{fmt(t.salesAmt)}</td>
                <td>{fmt(t.otherOutQty)}</td><td>{fmt(t.otherOutAmt)}</td>
                <td />
                <td>{fmt(t.closingQty)}</td><td>{fmt(t.closingAmt)}</td>
                <td /><td />
              </tr>
            )}
          </tbody>
        </table>
        {rows.length > 800 && <div className="hint">상위 800건만 표시 중 — 검색으로 좁혀주세요 (합계는 전체 기준).</div>}
      </div>
      {toast}
    </>
  )
}
